import {
  Accordion, Alert, Anchor, Badge, Box, Button, Chip, Grid, Group, Loader, MultiSelect, Paper, ScrollArea, SegmentedControl,
  Select, Slider, Stack, Switch, Table, Text, TextInput, Title, Tooltip,
} from "@mantine/core";
import {
  IconAlertTriangle, IconCalculator, IconDownload, IconInfoCircle, IconPhoto, IconSatellite, IconSettings,
} from "@tabler/icons-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Map as OlMap, View } from "ol";
import { defaults as defaultControls } from "ol/control";
import GeoJSON from "ol/format/GeoJSON";
import ImageLayer from "ol/layer/Image";
import TileLayer from "ol/layer/Tile";
import VectorLayer from "ol/layer/Vector";
import ImageStatic from "ol/source/ImageStatic";
import VectorSource from "ol/source/Vector";
import XYZ from "ol/source/XYZ";
import { Fill, Stroke, Style } from "ol/style";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router";

import { authFetch, errorMessage } from "../../api/client";
import { http } from "../../api/http";
import { useCan } from "../../auth/user";
import { PALETTE } from "../../components/Charts";
import { fmtNum } from "../../components/ui";
import { useT } from "../../i18n/script";
import UavIndexPanel from "./UavIndexPanel";

// ------------------------------------------------------------------------------------------------ tipovi
interface S2Status {
  configured: boolean; enabled: boolean; base_url: string; month_requests: number; month_cache_hits: number;
  month_limit: number | null; month_pu?: number; pu_limit?: number; defaults: { max_cloud: number; resolution: number; aggregation: string };
}
interface IndexDef {
  code: string; name: string; target: string; description: string | null; formula: string;
  value_min: number; value_max: number; colormap: string;
}
export interface SeriesPoint {
  date: string; mean: number | null; min?: number | null; max?: number | null; std?: number | null; median?: number | null;
  p10?: number | null; p90?: number | null; pixel_count?: number | null; cloud_free_pct?: number | null;
}
interface StatsResult {
  indices: string[]; formulas: Record<string, string>; names: Record<string, string>;
  series: Record<string, SeriesPoint[]>; cached: boolean; cached_at?: string; params: Record<string, unknown>;
}
interface Observation {
  index_code: string; obs_date: string; source: string; source_ref: string | null; mean: number | null; median: number | null;
  min: number | null; max: number | null; pixel_count: number | null;
}
interface Scene { date: string; cloud: number | null; id: string }

const iso = (d: Date) => d.toISOString().slice(0, 10);
const RAMPS: Record<string, string[]> = {
  rdylgn: ["#a50026", "#f46d43", "#fee08b", "#a6d96a", "#006837"],
  viridis: ["#440154", "#21918c", "#fde725"],
  blues: ["#f7fbff", "#6baed6", "#08306b"],
  brbg: ["#8c510a", "#f5f5f5", "#01665e"],
  brbg_r: ["#01665e", "#f5f5f5", "#8c510a"],
  oranges: ["#fff5eb", "#7f2704"],
};

type Preset = "sezona" | "6m" | "12m" | "prosla";
function presetRange(p: Preset): [string, string] {
  const now = new Date();
  const y = now.getFullYear();
  const mar1 = new Date(Date.UTC(y, 2, 1));
  switch (p) {
    case "sezona": return now >= mar1 ? [`${y}-03-01`, iso(now)] : [`${y - 1}-03-01`, iso(now)];
    case "6m": return [iso(new Date(now.getTime() - 182 * 86400_000)), iso(now)];
    case "12m": return [iso(new Date(now.getTime() - 365 * 86400_000)), iso(now)];
    case "prosla": return [`${y - 1}-03-01`, `${y - 1}-10-31`];
  }
}

/** Kartica „Satelit“ u kartonu voćnjaka: Sentinel-2 indeksi (vremenska serija, snimci, slika indeksa). */
export default function SatelliteTab({ orchardId, siteId }: { orchardId: number; siteId?: number | null }) {
  const { t } = useT();
  const can = useCan();
  const analyst = can("sentinel:analyze");

  const status = useQuery({ queryKey: ["s2-status"], queryFn: () => http.get<S2Status>("/api/v1/sentinel/status") });
  const defs = useQuery({ queryKey: ["index-defs"], staleTime: 600_000,
    queryFn: () => http.get<IndexDef[]>("/api/v1/indices/definitions") });
  const obs = useQuery({ queryKey: ["s2-observations", orchardId],
    queryFn: () => http.get<Observation[]>("/api/v1/sentinel/observations", { target_type: "orchard", target_id: orchardId }) });

  // ---------------------------------------------------------------- parametri
  const [codes, setCodes] = useState<string[]>(["NDVI", "NDRE", "NDMI"]);
  const [preset, setPreset] = useState<Preset | "">("sezona");
  const [[dateFrom, dateTo], setRange] = useState<[string, string]>(presetRange("sezona"));
  const [cloud, setCloud] = useState(30);
  const [resolution, setResolution] = useState("10");
  const [aggregation, setAggregation] = useState("P5D");
  const [pctl, setPctl] = useState(true);
  const [useCache, setUseCache] = useState(true);
  const [save, setSave] = useState(true);
  const defaultsApplied = useRef(false);
  useEffect(() => {
    const d = status.data?.defaults;
    if (!d || defaultsApplied.current) return;
    defaultsApplied.current = true;
    if (d.max_cloud != null) setCloud(d.max_cloud);
    if (d.resolution) setResolution(String(d.resolution));
    if (d.aggregation) setAggregation(d.aggregation);
  }, [status.data]);
  // indeksi koji ne postoje u katalogu se izbacuju iz podrazumijevanog izbora
  useEffect(() => {
    if (defs.data) setCodes((c) => c.filter((x) => defs.data!.some((d) => d.code === x)));
  }, [defs.data]);
  const defBy = useMemo(() => Object.fromEntries((defs.data ?? []).map((d) => [d.code, d])), [defs.data]);

  const stats = useMutation({
    mutationFn: () => http.post<StatsResult>("/api/v1/sentinel/stats", {
      target_type: "orchard", target_id: orchardId, index_codes: codes, date_from: dateFrom, date_to: dateTo,
      aggregation, max_cloud_coverage: cloud, resolution: Number(resolution), percentiles: pctl ? [10, 50, 90] : [],
      save, use_cache: useCache,
    }),
    onSuccess: () => { status.refetch(); if (save) obs.refetch(); },
  });
  const scenes = useMutation({
    mutationFn: () => http.get<Scene[]>("/api/v1/sentinel/scenes", {
      target_type: "orchard", target_id: orchardId, date_from: dateFrom, date_to: dateTo, max_cloud: cloud }),
    onSuccess: () => status.refetch(),
  });

  const ready = !!status.data?.configured && !!status.data?.enabled;

  // sačuvana istorija (sve ranije analize) kao serije po indeksu
  const history = useMemo(() => {
    const out: Record<string, SeriesPoint[]> = {};
    for (const o of obs.data ?? []) {
      const arr = (out[o.index_code] ??= []);
      if (arr.some((p) => p.date === o.obs_date)) continue;
      arr.push({ date: o.obs_date, mean: o.mean, median: o.median, min: o.min, max: o.max, pixel_count: o.pixel_count });
    }
    Object.values(out).forEach((a) => a.sort((a1, b1) => a1.date.localeCompare(b1.date)));
    return out;
  }, [obs.data]);

  const result = stats.data;
  const shownSeries = result?.series ?? history;
  const shownNames = result?.names ?? Object.fromEntries(Object.keys(history).map((c) => [c, defBy[c]?.name ?? c]));

  return (
    <Stack gap="md">
      <UavIndexPanel orchardId={orchardId} siteId={siteId} onSaved={() => obs.refetch()} />
      <Group justify="space-between" align="flex-end" wrap="wrap">
        <div>
          <Group gap={8}><IconSatellite size={20} color="var(--mantine-color-agri-8, #1f4d36)" />
            <Title order={4}>{t("Satelitski indeksi (Sentinel-2 L2A)")}</Title></Group>
          <Text size="sm" c="dimmed">{t("Vremenska serija vegetacijskih indeksa za granicu voćnjaka, sa maskiranim oblacima.")}</Text>
        </div>
        {status.data && (
          <Group gap={6}>
            <Badge variant="light" color={ready ? "green" : "gray"}>{ready ? t("Izvor podešen") : t("Izvor nije podešen")}</Badge>
            <Tooltip label={t("Upiti prema Copernicusu ovog mjeseca (keš ne troši kvotu)")}>
              <Badge variant="outline" color="gray">
                {t("Upiti")}: {status.data.month_requests}{status.data.month_limit ? ` / ${status.data.month_limit}` : ""} · PU: {Math.round(status.data.month_pu ?? 0)}{status.data.pu_limit ? ` / ${status.data.pu_limit}` : ""}
                {" · "}{t("iz keša")}: {status.data.month_cache_hits}
              </Badge>
            </Tooltip>
          </Group>
        )}
      </Group>

      {status.data && !ready && (
        <Alert color="yellow" variant="light" icon={<IconAlertTriangle />} title={t("Satelitski izvor nije podešen")}>
          <Text size="sm">
            {t("Za nove analize administrator treba da unese besplatan Copernicus Data Space OAuth klijent (client id i secret) u Administracija → Podešavanja → Satelitski podaci i da uključi satelitske analize.")}
            {" "}{t("Do tada su prikazane samo ranije sačuvane vrijednosti.")}
          </Text>
          {can("system:params") && (
            <Button component={Link} to="/admin/podesavanja?sekcija=satelit" size="xs" mt="sm" variant="light"
              leftSection={<IconSettings size={14} />}>{t("Otvori podešavanja satelita")}</Button>
          )}
        </Alert>
      )}

      <Grid gap="md">
        {analyst && (
          <Grid.Col span={{ base: 12, md: 4 }}>
            <Paper withBorder radius="lg" p="md">
              <Stack gap="sm">
                <Text fw={700}>{t("Parametri analize")}</Text>
                <MultiSelect label={t("Indeksi")} data={(defs.data ?? []).map((d) => ({ value: d.code, label: `${d.code} — ${d.name}` }))}
                  value={codes} onChange={setCodes} maxValues={6} searchable
                  renderOption={({ option }) => {
                    const d = defBy[option.value];
                    return (
                      <div>
                        <Text size="sm">{option.label}</Text>
                        {d && <Text size="xs" c="dimmed" ff="monospace" data-no-translit>{d.formula}</Text>}
                      </div>
                    );
                  }}
                  description={codes.length ? codes.map((c) => defBy[c] ? `${c} = ${defBy[c].formula}` : c).join(" · ") : undefined}
                  descriptionProps={{ "data-no-translit": "true" }} />
                <div>
                  <Text size="sm" fw={500} mb={4}>{t("Period")}</Text>
                  <SegmentedControl fullWidth size="xs" value={preset}
                    onChange={(v) => { setPreset(v as Preset); setRange(presetRange(v as Preset)); }}
                    data={[{ value: "sezona", label: t("Ova sezona") }, { value: "6m", label: t("6 mjeseci") },
                      { value: "12m", label: t("12 mjeseci") }, { value: "prosla", label: t("Prošla sezona") }]} />
                  <Group grow gap="xs" mt="xs">
                    <TextInput type="date" size="xs" aria-label={t("Od")} value={dateFrom}
                      onChange={(e) => { const v = e.currentTarget.value; setPreset(""); setRange(([, b]) => [v, b]); }} />
                    <TextInput type="date" size="xs" aria-label={t("Do")} value={dateTo}
                      onChange={(e) => { const v = e.currentTarget.value; setPreset(""); setRange(([a]) => [a, v]); }} />
                  </Group>
                </div>
                <div>
                  <Group justify="space-between"><Text size="sm" fw={500}>{t("Maksimalna oblačnost snimka")}</Text>
                    <Text size="sm" c="dimmed">{cloud}%</Text></Group>
                  <Slider min={0} max={100} step={5} value={cloud} onChange={setCloud} label={(v) => `${v}%`}
                    marks={[{ value: 0, label: "0" }, { value: 50, label: "50" }, { value: 100, label: "100" }]} mb="md" />
                </div>
                <Group grow gap="xs">
                  <Select size="xs" label={t("Rezolucija")} value={resolution} onChange={(v) => v && setResolution(v)} allowDeselect={false}
                    data={[{ value: "10", label: "10 m" }, { value: "20", label: "20 m" }, { value: "60", label: "60 m" }]} />
                  <Select size="xs" label={t("Interval")} value={aggregation} onChange={(v) => v && setAggregation(v)} allowDeselect={false}
                    data={[{ value: "P1D", label: t("Svaki snimak") }, { value: "P5D", label: t("5 dana") },
                      { value: "P10D", label: t("10 dana") }, { value: "P1M", label: t("Mjesec") }]} />
                </Group>
                <Switch size="sm" checked={pctl} onChange={(e) => setPctl(e.currentTarget.checked)} label={t("Percentili (p10, medijana, p90)")} />
                <Switch size="sm" checked={useCache} onChange={(e) => setUseCache(e.currentTarget.checked)} label={t("Koristi keš")} />
                <Switch size="sm" checked={save} onChange={(e) => setSave(e.currentTarget.checked)} label={t("Sačuvaj u istoriju voćnjaka")} />
                <Button leftSection={<IconCalculator size={16} />} loading={stats.isPending} disabled={!codes.length || !dateFrom || !dateTo}
                  onClick={() => stats.mutate()}>{t("Izračunaj")}</Button>
                {stats.isError && <Alert color="red" variant="light" icon={<IconAlertTriangle />}>{errMsg(stats.error)}</Alert>}
              </Stack>
            </Paper>
          </Grid.Col>
        )}

        <Grid.Col span={{ base: 12, md: analyst ? 8 : 12 }}>
          <Paper withBorder radius="lg" p="md" style={{ opacity: stats.isPending ? 0.55 : 1, transition: "opacity .2s" }}>
            <Group justify="space-between" mb="xs" wrap="wrap">
              <div>
                <Text fw={700}>{result ? t("Rezultat analize") : t("Sačuvana istorija indeksa")}</Text>
                <Text size="xs" c="dimmed">
                  {result
                    ? `${String(result.params.from)} – ${String(result.params.to)} · ${String(result.params.aggregation)} · ${String(result.params.resolution)} m · ≤${String(result.params.max_cloud)}% ${t("oblačnosti")}`
                    : t("Srednja vrijednost po datumu iz svih ranijih analiza")}
                </Text>
              </div>
              <Group gap={6}>
                {result?.cached && <Tooltip label={result.cached_at ? `${t("Izračunato")} ${new Date(result.cached_at).toLocaleString("sr-Latn-BA")}` : ""}>
                  <Badge color="blue" variant="light">{t("iz keša")}</Badge></Tooltip>}
                {result && <Button size="compact-xs" variant="subtle" onClick={() => stats.reset()}>{t("Prikaži istoriju")}</Button>}
                <Button size="compact-sm" variant="light" leftSection={<IconDownload size={14} />}
                  disabled={!Object.values(shownSeries).some((s) => s.length)}
                  onClick={() => downloadCsv(shownSeries, `satelit_vocnjak_${orchardId}.csv`)}>{t("Preuzmi CSV")}</Button>
              </Group>
            </Group>
            {obs.isLoading && !result ? <Loader size="sm" /> : (
              <>
                <IndexChart series={shownSeries} names={shownNames} />
                <SeriesTable series={shownSeries} />
              </>
            )}
          </Paper>
        </Grid.Col>
      </Grid>

      {analyst && (
        <Paper withBorder radius="lg" p="md">
          <Group justify="space-between" mb="sm" wrap="wrap">
            <div>
              <Text fw={700}>{t("Dostupni snimci")}</Text>
              <Text size="xs" c="dimmed">{t("Sentinel-2 preleti nad voćnjakom u izabranom periodu; klik na datum prikazuje kartu indeksa.")}</Text>
            </div>
            <Button size="xs" variant="light" leftSection={<IconPhoto size={14} />} loading={scenes.isPending}
              onClick={() => scenes.mutate()}>{t("Dostupni snimci")}</Button>
          </Group>
          {scenes.isError && <Alert color="red" variant="light" icon={<IconAlertTriangle />} mb="sm">{errMsg(scenes.error)}</Alert>}
          {scenes.data && (scenes.data.length === 0
            ? <Text size="sm" c="dimmed">{t("Nema snimaka u periodu sa zadatom oblačnošću.")}</Text>
            : <SceneViewer orchardId={orchardId} scenes={scenes.data} codes={codes.length ? codes : ["NDVI"]} defBy={defBy} />)}
        </Paper>
      )}

      <Accordion variant="contained" radius="lg">
        <Accordion.Item value="kako">
          <Accordion.Control icon={<IconInfoCircle size={18} />}>{t("Kako se računa")}</Accordion.Control>
          <Accordion.Panel>
            <Stack gap={6}>
              <Text size="sm">{t("Za svaki snimak Sentinel-2 L2A (atmosferski korigovana refleksija) indeks se računa po pikselu unutar granice voćnjaka. Pikseli koje klasifikacija scene (SCL) označi kao oblak, sjenku oblaka, cirus ili snijeg se izbacuju, pa vrijednosti potiču samo od vidljivog tla i krošnji.")}</Text>
              <Text size="sm">{t("Interval određuje kako se snimci grupišu: „Svaki snimak“ daje vrijednost za svaki prelet, a 5 dana, 10 dana ili mjesec spajaju sve čiste piksele u tom periodu u jednu vrijednost — serija je mirnija i ima manje praznina.")}</Text>
              <Text size="sm">{t("Srednja vrijednost je prosjek čistih piksela. Percentili p10 i p90 pokazuju raspon u kojem je 80% piksela (traka oko linije): široka traka znači neujednačen voćnjak, npr. zbog praznih mjesta, trave među redovima ili stresa dijela stabala.")}</Text>
              <Text size="sm">{t("Besplatna Copernicus kvota se štiti kešom (isti upit u periodu čuvanja vraća se bez novog poziva) i mjesečnim limitom upita koji podešava administrator. Svi parametri se čuvaju uz rezultat radi ponovljivosti.")}</Text>
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </Stack>
  );
}

const errMsg = (e: unknown) => (e instanceof Error ? e.message : errorMessage(e));

// ------------------------------------------------------------------------------------------------ grafik
/** Linijski grafik srednje vrijednosti po indeksu sa trakom p10–p90, vremenska osa, krstić i opis na prelazu. */
export function IndexChart({ series, names, height = 300 }: { series: Record<string, SeriesPoint[]>; names: Record<string, string>; height?: number }) {
  const { t } = useT();
  const wrap = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState<number | null>(null);
  // boja prati indeks (ne redoslijed u odgovoru): NDVI, NDRE, NDMI... uvijek iste boje
  const codes = Object.keys(series).filter((c) => series[c].some((p) => p.mean != null)).sort(byIndexOrder);
  if (!codes.length) return <Text size="sm" c="dimmed" py="xl" ta="center">{t("Nema vrijednosti za prikaz — pokrenite analizu ili provjerite period.")}</Text>;

  const W = 800, H = height, L = 44, R = 64, T = 12, B = 30;
  const pts = codes.flatMap((c) => series[c]);
  const times = pts.map((p) => Date.parse(p.date));
  let t0 = Math.min(...times), t1 = Math.max(...times);
  if (t0 === t1) { t0 -= 86400_000 * 5; t1 += 86400_000 * 5; }
  const lo = Math.min(...pts.map((p) => p.p10 ?? p.mean ?? Infinity));
  const hi = Math.max(...pts.map((p) => p.p90 ?? p.mean ?? -Infinity));
  const step = niceStep((hi - lo) / 4 || 0.1);
  const y0 = Math.floor(lo / step) * step, y1 = Math.max(Math.ceil(hi / step) * step, y0 + step);
  const x = (d: string) => L + ((Date.parse(d) - t0) / (t1 - t0)) * (W - L - R);
  const y = (v: number) => T + (H - T - B) * (1 - (v - y0) / (y1 - y0));
  const ticks: number[] = [];
  for (let v = y0; v <= y1 + 1e-9; v += step) ticks.push(v);
  // mjesečne oznake na x osi
  const months: number[] = [];
  const m = new Date(t0); m.setUTCDate(1); m.setUTCHours(0, 0, 0, 0);
  if (m.getTime() < t0) m.setUTCMonth(m.getUTCMonth() + 1);
  const span = (t1 - t0) / (30 * 86400_000);
  const every = span > 14 ? 3 : span > 7 ? 2 : 1;
  for (let k = 0; m.getTime() <= t1; k++, m.setUTCMonth(m.getUTCMonth() + 1)) if (k % every === 0) months.push(m.getTime());
  const allDates = Array.from(new Set(pts.map((p) => p.date))).sort();
  const color = (i: number) => PALETTE[i % PALETTE.length];

  // direktne oznake na kraju linija (razmaknute da se ne preklapaju)
  const ends = codes.map((c, i) => {
    const s = series[c].filter((p) => p.mean != null);
    const last = s[s.length - 1];
    return { c, i, y: y(last.mean!), x: x(last.date) };
  }).sort((a, b) => a.y - b.y);
  for (let k = 1; k < ends.length; k++) if (ends[k].y - ends[k - 1].y < 13) ends[k].y = ends[k - 1].y + 13;

  const hoverDate = hover != null ? allDates[hover] : null;
  const onMove = (e: React.PointerEvent<SVGRectElement>) => {
    const r = (e.currentTarget.ownerSVGElement as SVGSVGElement).getBoundingClientRect();
    const px = ((e.clientX - r.left) / r.width) * W;
    let best = 0, bd = Infinity;
    allDates.forEach((d, i) => { const dd = Math.abs(x(d) - px); if (dd < bd) { bd = dd; best = i; } });
    setHover(best);
  };
  const hx = hoverDate ? x(hoverDate) : 0;

  return (
    <Box ref={wrap} pos="relative">
      <Group gap="md" mb={4}>
        {codes.map((c, i) => (
          <Group key={c} gap={6}>
            <svg width={18} height={10}><line x1={0} x2={18} y1={5} y2={5} stroke={color(i)} strokeWidth={2.5} /></svg>
            <Text size="xs">{c}{names[c] && names[c] !== c ? ` — ${names[c]}` : ""}</Text>
          </Group>
        ))}
        {pts.some((p) => p.p10 != null) && (
          <Group gap={6}><Box w={18} h={10} bg="gray.3" style={{ borderRadius: 2 }} /><Text size="xs" c="dimmed">p10–p90</Text></Group>
        )}
      </Group>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label={t("Vremenska serija indeksa")} style={{ fontFamily: "inherit", fontSize: 11, display: "block" }}>
        {ticks.map((v) => (
          <g key={v}>
            <line x1={L} x2={W - R} y1={y(v)} y2={y(v)} stroke="var(--mantine-color-default-border)" strokeWidth={1} />
            <text x={L - 6} y={y(v) + 4} textAnchor="end" fill="var(--mantine-color-dimmed)">{v.toFixed(step < 0.1 ? 2 : 1)}</text>
          </g>
        ))}
        {months.map((ms) => {
          const d = new Date(ms);
          const xx = L + ((ms - t0) / (t1 - t0)) * (W - L - R);
          return (
            <text key={ms} x={xx} y={H - 10} textAnchor="middle" fill="var(--mantine-color-dimmed)">
              {`${d.getUTCMonth() + 1}/${String(d.getUTCFullYear()).slice(2)}`}
            </text>
          );
        })}
        {codes.map((c, i) => {
          const s = series[c].filter((p) => p.mean != null);
          const band = s.filter((p) => p.p10 != null && p.p90 != null);
          return (
            <g key={c}>
              {band.length > 1 && (
                <path fill={color(i)} fillOpacity={0.13} stroke="none"
                  d={`M${band.map((p) => `${x(p.date)},${y(p.p90!)}`).join("L")}L${[...band].reverse().map((p) => `${x(p.date)},${y(p.p10!)}`).join("L")}Z`} />
              )}
              <polyline fill="none" stroke={color(i)} strokeWidth={2} strokeLinejoin="round"
                points={s.map((p) => `${x(p.date)},${y(p.mean!)}`).join(" ")} />
              {s.length <= 60 && s.map((p) => (
                <circle key={p.date} cx={x(p.date)} cy={y(p.mean!)} r={3.5} fill={color(i)} stroke="var(--mantine-color-body)" strokeWidth={1.5} />
              ))}
            </g>
          );
        })}
        {codes.length <= 4 && ends.map((e) => (
          <text key={e.c} x={W - R + 6} y={e.y + 4} fill="var(--mantine-color-text)" fontWeight={600}>{e.c}</text>
        ))}
        {hoverDate && <line x1={hx} x2={hx} y1={T} y2={H - B} stroke="var(--mantine-color-dimmed)" strokeWidth={1} strokeDasharray="3 3" />}
        <rect x={L} y={T} width={W - L - R} height={H - T - B} fill="transparent"
          onPointerMove={onMove} onPointerLeave={() => setHover(null)} />
      </svg>
      {hoverDate && (
        <Paper shadow="md" p={8} radius="md" withBorder
          style={{ position: "absolute", top: 34, pointerEvents: "none", minWidth: 150,
            ...(hx / W > 0.6 ? { right: `${100 - (hx / W) * 100 + 2}%` } : { left: `${(hx / W) * 100 + 2}%` }) }}>
          <Text size="xs" c="dimmed" mb={2}>{new Date(hoverDate).toLocaleDateString("sr-Latn-BA")}</Text>
          {codes.map((c, i) => {
            const p = series[c].find((q) => q.date === hoverDate);
            return (
              <Group key={c} gap={6} wrap="nowrap">
                <svg width={12} height={8}><line x1={0} x2={12} y1={4} y2={4} stroke={color(i)} strokeWidth={2.5} /></svg>
                <Text size="sm" fw={700}>{p?.mean != null ? p.mean.toFixed(3) : "—"}</Text>
                <Text size="xs" c="dimmed">{c}{p?.p10 != null && p?.p90 != null ? ` (${p.p10.toFixed(2)}–${p.p90.toFixed(2)})` : ""}</Text>
              </Group>
            );
          })}
        </Paper>
      )}
    </Box>
  );
}

const INDEX_ORDER = ["NDVI", "NDRE", "NDMI", "GNDVI", "EVI", "SAVI", "MSAVI", "NDWI"];
const byIndexOrder = (a: string, b: string) => {
  const ia = INDEX_ORDER.indexOf(a), ib = INDEX_ORDER.indexOf(b);
  return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib) || a.localeCompare(b);
};

function niceStep(v: number): number {
  const e = 10 ** Math.floor(Math.log10(v));
  for (const k of [1, 2, 2.5, 5, 10]) if (v <= k * e) return k * e;
  return 10 * e;
}

// ------------------------------------------------------------------------------------------------ tabela i CSV
const COLS: [keyof SeriesPoint, string, number][] = [
  ["mean", "Srednja", 3], ["median", "Medijana", 3], ["p10", "p10", 3], ["p90", "p90", 3], ["min", "Min", 3], ["max", "Max", 3],
  ["std", "SD", 3], ["pixel_count", "Pikseli", 0], ["cloud_free_pct", "Bez oblaka %", 1],
];

function flatRows(series: Record<string, SeriesPoint[]>) {
  return Object.entries(series).flatMap(([code, rows]) => rows.map((r) => ({ code, ...r })))
    .sort((a, b) => a.date.localeCompare(b.date) || a.code.localeCompare(b.code));
}

function SeriesTable({ series }: { series: Record<string, SeriesPoint[]> }) {
  const { t } = useT();
  const rows = flatRows(series);
  if (!rows.length) return null;
  const cols = COLS.filter(([k]) => rows.some((r) => r[k] != null));
  return (
    <Accordion variant="default" mt="xs" chevronPosition="left">
      <Accordion.Item value="tabela" style={{ borderBottom: 0 }}>
        <Accordion.Control px={0}><Text size="sm" fw={600}>{t("Tabela vrijednosti")} ({rows.length})</Text></Accordion.Control>
        <Accordion.Panel>
          <ScrollArea.Autosize mah={340} type="auto">
            <Table striped highlightOnHover fz="xs" stickyHeader>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t("Datum")}</Table.Th><Table.Th>{t("Indeks")}</Table.Th>
                  {cols.map(([k, l]) => <Table.Th key={k} ta="right">{t(l)}</Table.Th>)}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((r) => (
                  <Table.Tr key={`${r.code}-${r.date}`}>
                    <Table.Td>{r.date}</Table.Td><Table.Td>{r.code}</Table.Td>
                    {cols.map(([k, , d]) => <Table.Td key={k} ta="right">{r[k] == null ? "—" : fmtNum(r[k], d)}</Table.Td>)}
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea.Autosize>
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
}

function downloadCsv(series: Record<string, SeriesPoint[]>, name: string) {
  const rows = flatRows(series);
  const head = ["datum", "indeks", ...COLS.map(([k]) => k)];
  const lines = [head.join(";"), ...rows.map((r) => [r.date, r.code, ...COLS.map(([k]) => (r[k] == null ? "" : String(r[k])))].join(";"))];
  const blob = new Blob(["﻿" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

// ------------------------------------------------------------------------------------------------ snimci i karta indeksa
const VISUAL_CODES = ["TRUE_COLOR", "FALSE_COLOR", "AGRICULTURE"];

function SceneViewer({ orchardId, scenes, codes, defBy }: {
  orchardId: number; scenes: Scene[]; codes: string[]; defBy: Record<string, IndexDef>;
}) {
  const { t } = useT();
  const [date, setDate] = useState<string | null>(null);
  const [code, setCode] = useState(codes[0]);
  const [img, setImg] = useState<{ url: string; extent: number[] } | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => { if (!codes.includes(code) && !VISUAL_CODES.includes(code)) setCode(codes[0]); }, [codes, code]);
  const vis = useQuery({ queryKey: ["s2-view-layers"], staleTime: 600_000,
    queryFn: () => http.get<{ visual: { code: string; name: string; legend: { color: string; label: string }[] }[] }>("/api/v1/sentinel/view/layers") });
  const visual = vis.data?.visual.find((v) => v.code === code);
  useEffect(() => {
    if (!date) return;
    let cancelled = false;
    setLoading(true); setErr(null);
    (async () => {
      try {
        const q = new URLSearchParams({ target_type: "orchard", target_id: String(orchardId), index_code: code, date });
        const r = await authFetch(`/api/v1/sentinel/image?${q}`);
        if (!r.ok) {
          const txt = await r.text();
          let msg = txt;
          try { msg = errorMessage(JSON.parse(txt)); } catch { /* tekst */ }
          throw new Error(msg || r.statusText);
        }
        const extent = (r.headers.get("X-Bbox-3857") ?? "").split(",").map(Number);
        if (extent.length !== 4 || extent.some((v) => !Number.isFinite(v))) throw new Error(t("Servis nije vratio obuhvat slike"));
        const url = URL.createObjectURL(await r.blob());
        if (!cancelled) setImg((old) => { if (old) URL.revokeObjectURL(old.url); return { url, extent }; });
      } catch (e) {
        if (!cancelled) setErr(errMsg(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [date, code, orchardId, t]);

  const def = defBy[code];
  return (
    <Stack gap="sm">
      <ScrollArea type="auto" offsetScrollbars>
        <Chip.Group value={date ?? ""} onChange={(v) => setDate(String(v) || null)}>
          <Group gap={6} wrap="nowrap">
            {scenes.map((s) => (
              <Chip key={s.date} value={s.date} size="xs" variant="outline">
                {new Date(s.date).toLocaleDateString("sr-Latn-BA", { day: "numeric", month: "numeric", year: "2-digit" })}
                {s.cloud != null ? ` · ☁ ${Math.round(s.cloud)}%` : ""}
              </Chip>
            ))}
          </Group>
        </Chip.Group>
      </ScrollArea>
      <Group gap="xs">
        <Select size="xs" w={220} value={code} onChange={(v) => v && setCode(v)} allowDeselect={false} aria-label={t("Indeks")}
          data={[{ group: t("Spektar"), items: [{ value: "TRUE_COLOR", label: t("Prirodne boje (vidljivi spektar)") },
            { value: "FALSE_COLOR", label: t("Lažne boje (NIR)") }, { value: "AGRICULTURE", label: t("Poljoprivreda (SWIR, NIR, plavi)") }] },
            { group: t("Indeksi"), items: codes.map((c) => ({ value: c, label: defBy[c] ? `${c} — ${defBy[c].name}` : c })) }]} />
        {!date && <Text size="xs" c="dimmed">{t("Izaberite datum snimka.")}</Text>}
        {loading && <Loader size="xs" />}
      </Group>
      {err && <Alert color="red" variant="light" icon={<IconAlertTriangle />}>{err}</Alert>}
      <Box pos="relative" h={380} style={{ borderRadius: 12, overflow: "hidden", border: "1px solid var(--mantine-color-default-border)" }}>
        <IndexMap orchardId={orchardId} image={img} />
        {!def && img && visual && (
          <Paper shadow="sm" p={8} radius="md" style={{ position: "absolute", left: 10, bottom: 10, zIndex: 2, maxWidth: 280 }}>
            <Text size="xs" fw={700} mb={4}>{t(visual.name)}{date ? ` · ${date}` : ""}</Text>
            {visual.legend.map((l) => (
              <Group key={l.label} gap={6} wrap="nowrap" align="flex-start">
                <span style={{ width: 12, height: 12, borderRadius: 3, background: l.color, flexShrink: 0, marginTop: 2, border: "1px solid rgba(0,0,0,.2)" }} />
                <Text size="10px" lh={1.3}>{t(l.label)}</Text>
              </Group>
            ))}
          </Paper>
        )}
        {def && img && (
          <Paper shadow="sm" p={8} radius="md" style={{ position: "absolute", left: 10, bottom: 10, zIndex: 2, minWidth: 180 }}>
            <Text size="xs" fw={700} mb={4}>{code}{date ? ` · ${date}` : ""}</Text>
            <Box h={10} style={{ borderRadius: 3, background: `linear-gradient(to right, ${(RAMPS[def.colormap] ?? RAMPS.rdylgn).join(",")})` }} />
            <Group justify="space-between" mt={2}>
              <Text size="10px" c="dimmed">{def.value_min}</Text>
              <Text size="10px" c="dimmed">{((def.value_min + def.value_max) / 2).toFixed(1)}</Text>
              <Text size="10px" c="dimmed">{def.value_max}</Text>
            </Group>
            <Text size="10px" c="dimmed">{t("Providno = oblak, sjenka ili snijeg")}</Text>
          </Paper>
        )}
      </Box>
      <Anchor size="xs" c="dimmed" href="https://dataspace.copernicus.eu" target="_blank" rel="noreferrer">
        {t("Sadrži izmijenjene Copernicus Sentinel podatke")}
      </Anchor>
    </Stack>
  );
}

/** Mala OpenLayers karta: Esri satelitska podloga, granica voćnjaka i slika indeksa (ImageStatic, EPSG:3857). */
function IndexMap({ orchardId, image }: { orchardId: number; image: { url: string; extent: number[] } | null }) {
  const el = useRef<HTMLDivElement>(null);
  const mapRef = useRef<OlMap | null>(null);
  const imgLayer = useRef(new ImageLayer({ zIndex: 5, opacity: 0.9 }));
  const boundary = useRef(new VectorSource());

  useEffect(() => {
    if (!el.current || mapRef.current) return;
    const map = new OlMap({
      target: el.current,
      controls: defaultControls({ attributionOptions: { collapsible: true } }),
      view: new View({ center: [1913000, 5584000], zoom: 15, maxZoom: 20 }),
      layers: [
        new TileLayer({ source: new XYZ({ url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
          maxZoom: 19, crossOrigin: "anonymous", attributions: "Esri, Maxar, Earthstar Geographics" }) }),
        imgLayer.current,
        new VectorLayer({ source: boundary.current, zIndex: 10,
          style: new Style({ stroke: new Stroke({ color: "#ffffff", width: 2.5 }), fill: new Fill({ color: "rgba(0,0,0,0)" }) }) }),
      ],
    });
    mapRef.current = map;
    return () => { map.setTarget(undefined); mapRef.current = null; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    http.get<{ type: string; features: unknown[] }>("/api/v1/orchards/geojson", { ids: [orchardId] }).then((fc) => {
      if (cancelled) return;
      boundary.current.clear();
      boundary.current.addFeatures(new GeoJSON().readFeatures(fc, { dataProjection: "EPSG:4326", featureProjection: "EPSG:3857" }) as never);
      const ext = boundary.current.getExtent();
      if (ext && Number.isFinite(ext[0])) mapRef.current?.getView().fit(ext, { padding: [40, 40, 40, 40], maxZoom: 18 });
    }).catch(() => undefined);
    return () => { cancelled = true; };
  }, [orchardId]);

  useEffect(() => {
    imgLayer.current.setSource(image ? new ImageStatic({ url: image.url, imageExtent: image.extent, projection: "EPSG:3857" }) : null);
    if (image && !boundary.current.getFeatures().length) mapRef.current?.getView().fit(image.extent, { padding: [20, 20, 20, 20] });
  }, [image]);

  return <div ref={el} style={{ position: "absolute", inset: 0 }} />;
}
